import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable"; // Asegúrate de tener este paquete instalado
import '../utils/Reportes.css';

const ReportForm = () => {
  const [selectedReport, setSelectedReport] = useState(""); // Tipo de reporte seleccionado
  const [reportData, setReportData] = useState(null); // Datos del reporte (puede ser null al principio)
  const [selectedDate, setSelectedDate] = useState(""); // Fecha para el reporte de dinero cobrado
  const [error, setError] = useState(""); // Manejo de errores
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [tipoReporte, setTipoReporte] = useState('dia');
  
  // Función para manejar el cambio del selector
  const handleReportChange = (e) => {
    setSelectedReport(e.target.value);
    setReportData(null); // Resetea los datos del reporte al cambiar
    setError("");
  };

  // Función para manejar el cambio de la fecha
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Llamada a la API para obtener los datos del reporte
  const fetchReportData = async (fecha) => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + '/api/reportes', {
        params: { fecha }
      });
      //const response = await axios.get('/api/reportes', { params: { fecha } });
      setReportData(response.data);  // Guarda los datos recibidos en el estado
    } catch (error) {
      console.error('Error al obtener los datos:', error);
    }
  };  
  
  // useEffect para hacer la llamada cuando `selectedDate` cambia
  useEffect(() => {
    if (selectedDate) {
      fetchReportData(selectedDate);  // Llamada a la API cuando se selecciona una fecha
    }
  }, [selectedDate]);  // Se ejecuta cuando `selectedDate` cambia

  // Función para generar la URL de la API según el tipo de reporte
  const generateUrl = () => {
    //const baseUrl = process.env.REACT_APP_API_URL; // Usa la variable de entorno
    //const baseUrl = "http://localhost:4000"; // Base URL general
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:4000";
    if (selectedReport === "medidores") {
      return `${baseUrl}/medidores`; // URL para el reporte de medidores
    } else if (selectedReport === "clientes") {
      return `${baseUrl}/clientes`; // URL para el reporte de clientes
    } else if (selectedReport === "suma-total") {
      // URL para el reporte de suma total con fecha
      return `${baseUrl}/pagos/suma-total?fecha=${selectedDate}`;
      
    } else if (selectedReport === "reporte-rango") {
      return `${baseUrl}/reporte?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`;
    }

    return "";
  };

  // Función para generar el reporte
  const generateReport = async () => {
    if (!selectedReport) {
      setError("Por favor, selecciona un tipo de reporte.");
      return;
    }

    if ((selectedReport === "dinero" || selectedReport === "suma-total") && !selectedDate) {
      setError("Por favor, selecciona una fecha para el reporte.");
      return;
    }

    if (selectedReport === "reporte-rango" && (!fechaInicio || !fechaFin)) {
      setError("Por favor, selecciona ambas fechas para el rango.");
      return;
    }

    try {
      setError(""); // Reseteamos cualquier error previo
      const url = generateUrl();
      const response = await axios.get(url);
      setReportData(response.data);
    } catch (err) {
      setError("Error al generar el reporte. Por favor, intenta de nuevo.");
      console.error(err);
    }
  };

function obtenerNombreMesCustom(anio, mes) {
  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];
  const mesIndex = parseInt(mes, 10) - 1;
  return `${meses[mesIndex]} ${anio}`;
}

function agruparPorMes(datos, tipo = "Ingreso", campoFecha = "fecha") {
  return datos.reduce((acc, item) => {
    const fechaStr = item[campoFecha];
    if (!fechaStr || typeof fechaStr !== 'string') return acc;

    const partes = fechaStr.split('-');
    if (partes.length < 2) return acc;

    const [anio, mes] = partes;
    const mesClave = `${anio}-${mes}`;
    const mesNombre = obtenerNombreMesCustom(anio, mes);

    if (!acc[mesClave]) {
      acc[mesClave] = {
        descripcion: `${tipo} de ${mesNombre}`,
        cantidad: 0,
        precio_total: 0,
        fecha: mesNombre
      };
    }

    acc[mesClave].cantidad += parseInt(String(item.cantidad || '0').replace(/,/g, ''), 10) || 0;
    acc[mesClave].precio_total += parseFloat(String(item.precio_total || '0').replace(/,/g, '')) || 0;

    return acc;
  }, {});
}

const formatoMoneda = (valor) => {
  if (valor == null) return "0.00";
  return new Intl.NumberFormat("es-EC", { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(valor);
};

function agruparPorMesConsumos(datos) {
  const agrupado = datos.reduce((acc, item) => {
    if (!item.fecha_pago || !item.consumo_total || !item.nombre_cliente) return acc;

    const [anio, mes] = item.fecha_pago.split('-');
    if (!anio || !mes) return acc;

    const mesClave = `${anio}-${mes}`;
    const mesNombre = obtenerNombreMesCustom(anio, mes);

    if (!acc[mesClave]) {
      acc[mesClave] = {
        clientesSet: new Set(),
        consumo_total: 0,
        fecha_pago: mesNombre
      };
    }

    acc[mesClave].clientesSet.add(item.nombre_cliente);
    acc[mesClave].consumo_total += parseFloat(String(item.consumo_total).replace(/,/g, '')) || 0;

    return acc;
  }, {});

  const resultado = Object.values(agrupado).map((item) => ({
    nombre_cliente: item.clientesSet.size,
    consumo_total: item.consumo_total,
    fecha_pago: item.fecha_pago
  }));

  //console.log("Resultado agrupado:", resultado);

  return resultado;
}

  // Función para renderizar la tabla
  const renderTable = () => {
    if (selectedReport === "medidores") {
      return (
        <div className="clientes-panel">
          <div className="clientes-panel1">
            <table className="clientes-table">
              <thead className="clientes-table thead">
                <tr>
                  <th>#</th>
                  <th>Código Medidor</th>
                  <th>Ubicación</th>
                  <th>Fecha Instalación</th>
                  <th>Nombre</th>
                  <th>Estado</th>
                  <th>Cliente</th>
                  <th>Tarifa</th>
                </tr>
              </thead>
              <tbody>
                {reportData?.map((medidor, index) => (
                  <tr key={medidor.id_medidor}>
                    <td>{index + 1}</td>
                    <td>{medidor.codigo_medidor}</td>
                    <td>{medidor.ubicacion_medidor}</td>
                    <td>{medidor.fecha_instalacion}</td>
                    <td>{medidor.nombre_medidor}</td>
                    <td>{medidor.estado_medidor}</td>
                    <td>{medidor.cliente.nombre_cliente}</td>
                    <td>{medidor.tarifa.tipo_tarifa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (selectedReport === "clientes") {
      return (
        <div className="clientes-panel">
          <div className="clientes-panel1">
            <table className="clientes-table">
              <thead className="clientes-table thead">
                <tr>
                  <th>#</th>    
                  <th>Nombre</th>
                  <th>Cédula del Cliente</th>
                  <th>Dirección</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Fecha Registro</th>
                </tr>
              </thead>
              <tbody>
                {reportData?.map((cliente, index) => (
                  <tr key={cliente.id_cliente}>
                    <td>{index + 1}</td>
                    <td>{cliente.nombre_cliente}</td>
                    <td>{cliente.codigo_cliente}</td>
                    <td>{cliente.direccion_cliente}</td>
                    <td>{cliente.telefono_cliente}</td>
                    <td>{cliente.email_cliente}</td>
                    <td>{cliente.fecha_registro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (selectedReport === "suma-total") {
      // Verificación de si existen datos
      if (!reportData?.pagos?.length) {
        return <div>No se encontraron pagos para esta fecha.</div>;
      }

      return (

        <div className="report-panel">
          <h4>Detalle de Pagos Realizados</h4>
          <div className="mt-3">
            <p><strong>Fecha de cobro: </strong> {selectedDate} </p>
            <p><strong>Total Consumo: </strong> {reportData.totalConsumo} m3</p>
            <p><strong>Total Exceso: </strong> {reportData.totalExceso} m3</p>
            <p><strong>Total de Dinero Cobrado: $</strong> {reportData.total}</p>
          </div>
          <table className="table report-table">
            <thead  className="clientes-table thead" style={{ backgroundColor: '#3f92eb', color: 'white' }}>
              <tr>
                <th>#</th>
                <th>Nombre del Cliente</th>
                <th>Código Medidor</th>
                <th>Lectura Anterior</th>
                <th>Lectura Actual</th>
                <th>Consumo Total</th>
                <th>Exceso</th>
                <th>Mora</th>
                <th>Total a Pagar</th>
              </tr>
            </thead>
            <tbody>
              {reportData.pagos.map((pago, index) => (
                <tr key={pago.id_pago}>
                  <td>{index + 1}</td>
                  <td>{pago.nombre_cliente}</td>
                  <td>{pago.codigo_medidor}</td>
                  <td>{pago.lectura_anterior}</td>
                  <td>{pago.lectura_actual}</td>
                  <td>{pago.consumo_total}</td>
                  <td>{pago.exceso}</td>
                  <td>{pago.mora}</td>
                  <td>{pago.total_pagar}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <br /><br /><br /><br /><br /><br /> 
          <div></div>
        </div>
      );
    } else if (selectedReport === "reporte-rango") {
      if (!reportData) {
        return <div>No se encontraron datos en el rango seleccionado.</div>;
      }

      const { ingresos, egresos, deudas, consumos } = reportData;
      // Aquí agregas la agrupación (antes del return)
      const egresosMensuales = Object.values(agruparPorMes(egresos.egresos, "Egreso", "fecha"));
      const ingresosMensuales = Object.values(agruparPorMes(ingresos.ingresos, "Ingreso", "fecha"));
      const deudasMensuales = Object.values(agruparPorMes(deudas.deudas, "Deudas por cobrar", "fecha"));
      const consumosMensuales = Object.values(agruparPorMesConsumos(consumos.pagos)); 
      
      return (
        <div className="report-panel">
          <h4>Libro contable del {fechaInicio} al {fechaFin}</h4>
          {/* Tabla 1 - Egresos */}
          <h5>Tabla 1 - Egresos</h5>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Descripción</th>
                <th>Cantidad</th>
                <th>Precio Total</th>
                <th>Fechas</th>
              </tr>
            </thead>
            <tbody>
              {(tipoReporte === "mes" ? egresosMensuales : egresos.egresos).map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.descripcion}</td>
                  <td>{item.cantidad}</td>
                  <td>{formatoMoneda(item.precio_total)}</td>
                  <td>{item.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p><strong>Total Egresos: </strong> {egresos.valor_total}$</p>

          {/* Tabla 2 - Ingresos */}
          <h5>Tabla 2 - Ingresos</h5>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Descripción</th>
                <th>Cantidad</th>
                <th>Precio Total</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {(tipoReporte === "mes" ? ingresosMensuales : ingresos.ingresos).map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.descripcion}</td>
                  <td>{item.cantidad}</td>
                  <td>{item.precio_total}</td>
                  <td>{item.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p><strong>Total Ingresos: </strong> {ingresos.valor_total}$</p>

          {/* Tabla 3 - Deudas por cobrar */}
          <h5>Tabla 3 - Deudas por cobrar</h5>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Descripción</th>
                <th>Cantidad</th>
                <th>Precio Total</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {(tipoReporte === "mes" ? deudasMensuales : deudas.deudas).map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.descripcion}</td>
                  <td>{item.cantidad}</td>
                  <td>{item.precio_total}</td>
                  <td>{item.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p><strong>Total Deudas por cobrar: </strong> {deudas.valor_total}$</p>

          {/* Tabla 4 - Consumos */}
          <h5>Tabla 4 - Consumos Mensuales</h5>
          <table className="clientes-table">
            <thead>
              <tr>
                <th>#</th>
                <th>N. Clientes</th>
                <th>Monto</th>
                <th>Fecha Pago</th>
              </tr>
            </thead>
            <tbody>
              {consumosMensuales.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.nombre_cliente}</td>
                  <td>{item.consumo_total}</td>
                  <td>{item.fecha_pago}</td>
                </tr>
              ))}

            </tbody>
          </table>
          <p><strong>Total Consumos: </strong> {consumos.valor_total}$</p>
          <br /><br /><br /><br /><br /><br /> 
          <br /><br /><br /><br /><br /><br /> 
        </div>
      );
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
  
    if (selectedReport === "medidores" && reportData) {
      // Título del reporte
      doc.text("Reporte de Medidores", doc.internal.pageSize.width / 2, 20, { align: "center" });
  
      // Generación de tabla
      doc.autoTable({
        head: [
          [
            "#",
            "Código Medidor",
            "Ubicación",
            "Fecha Instalación",
            "Nombre",
            "Estado",
            "Cliente",
            "Tarifa",
          ],
        ],
        body: reportData.map((medidor, index) => [
          index + 1,
          medidor.codigo_medidor,
          medidor.ubicacion_medidor,
          medidor.fecha_instalacion,
          medidor.nombre_medidor,
          medidor.estado_medidor,
          medidor.cliente.nombre_cliente,
          medidor.tarifa.tipo_tarifa,
        ]),
        startY: 30,
      });
  
      doc.save("reporte_medidores.pdf");
    } else if (selectedReport === "clientes" && reportData) {
      // Título del reporte
      doc.text("Reporte de Clientes", doc.internal.pageSize.width / 2, 20, { align: "center" });
      // Generación de tabla
      doc.autoTable({
        head: [
          [
            "#",
            "Nombre",
            "Cédula del Cliente",
            "Dirección",
            "Teléfono",
            "Email",
            "Fecha Registro",
          ],
        ],
        body: reportData.map((cliente, index) => [
          index + 1,
          cliente.nombre_cliente,
          cliente.codigo_cliente,
          cliente.direccion_cliente,
          cliente.telefono_cliente,
          cliente.email_cliente,
          cliente.fecha_registro,
        ]),
        startY: 30,
        columnStyles: {
          1: { cellWidth: 40 }, // Ajusta el ancho de la columna "Nombre"
        },
      });
  
      doc.save("reporte_clientes.pdf");
    } else if (selectedReport === "suma-total" && reportData) {
      // Título del reporte
      doc.text("Reporte de Suma Total", doc.internal.pageSize.width / 2, 20, { align: "center" });
    
      // Generación de tabla
      doc.autoTable({
        head: [
          [
            "#",
            "Nombre del Cliente",
            "Código Medidor",
            "Lectura Anterior",
            "Lectura Actual",
            "Consumo Total",
            "Exceso",
            "Mora",
            "Total a Pagar",
          ],
        ],
        body: reportData.pagos.map((pago, index) => [
          index + 1,
          pago.nombre_cliente,
          pago.codigo_medidor,
          pago.lectura_anterior,
          pago.lectura_actual,
          pago.consumo_total,
          pago.exceso,
          pago.mora,
          pago.total_pagar,
        ]),
        startY: 30,
        columnStyles: {
          1: { cellWidth: 48 },
        },
      });
    
      // Calcular posición actual
      const totalHeight = doc.lastAutoTable.finalY + 10;
    
      // Dimensiones de la página
      const pageHeight = doc.internal.pageSize.height;
    
      // Espacio que necesitas para los textos
      const espacioNecesario = 50; // Por si escribes 4 líneas de texto
    
      // Verificar si hay espacio suficiente
      if (totalHeight + espacioNecesario > pageHeight) {
        doc.addPage();
        // Reiniciar altura para la nueva página
        doc.text(`Fecha de Cobro: ${selectedDate}`, 14, 20);
        doc.text(`Total Consumo m3: ${reportData?.totalConsumo || 0}`, 14, 30);
        doc.text(`Total Exceso m3: ${reportData?.totalExceso || 0}`, 14, 40);
        doc.text(`Total de Dinero Cobrado $: ${reportData?.total || 0}`, 14, 50);
      } else {
        // Hay espacio suficiente, continuar en la misma página
        doc.text(`Fecha de Cobro: ${selectedDate}`, 14, totalHeight + 10);
        doc.text(`Total Consumo m3: ${reportData?.totalConsumo || 0}`, 14, totalHeight + 20);
        doc.text(`Total Exceso m3: ${reportData?.totalExceso || 0}`, 14, totalHeight + 30);
        doc.text(`Total de Dinero Cobrado $: ${reportData?.total || 0}`, 14, totalHeight + 40);
      }
    
      doc.save("reporte_suma_total.pdf");
    }
    else if (selectedReport === "reporte-rango" && reportData) {
      doc.text(`JUNTA ADMINISTRADORA "SANTA CRUZ DE GUAMOTE" `, doc.internal.pageSize.width / 2, 20, { align: "center" });
      doc.text(`Libro Contable del Periodo: ${fechaInicio} al ${fechaFin}`, doc.internal.pageSize.width / 2, 30, { align: "center" });

      const egresos = reportData.egresos?.egresos ?? [];
      const ingresos = reportData.ingresos?.ingresos ?? [];
      const deudas = reportData.deudas?.deudas ?? [];
      const consumos = reportData.consumos?.pagos ?? [];

      // Variables para los totales (deben estar accesibles globalmente en este bloque)
      let totalIngresos = 0;
      let totalConsumos = 0;
      let totalEgresos = 0;
      let totalDeudas = 0;

        // Siempre agrupar consumos por mes (para día o mes)
      const consumosMensuales = Object.values(agruparPorMesConsumos(consumos));
      let y = 40;

      if (tipoReporte === "mes") {
        const egresosMensuales = Object.values(agruparPorMes(egresos, "Egreso"));
        const ingresosMensuales = Object.values(agruparPorMes(ingresos, "Ingreso"));
        const deudasMensuales = Object.values(agruparPorMes(deudas, "Deudas por cobrar"));

        // Tabla de Ingresos por mes
        doc.setFontSize(14);
        doc.text("Tabla de Ingresos", doc.internal.pageSize.width / 2, y, { align: "center" });
        y += 5;
        doc.autoTable({
          head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
          body: ingresos.map((item, i) => [
            i + 1,
            item.descripcion ?? "N/A",
            Number(item.cantidad ?? 0).toFixed(0),
            Number(item.precio_total ?? 0).toFixed(2),
            item.fecha ?? "N/A",
          ]),
          startY: y,
        });
        totalIngresos = ingresos.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
        doc.text(`Total Ingresos: $${totalIngresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

        // Tabla de Egresos por mes
        y = doc.lastAutoTable.finalY + 15;
        doc.text("Tabla de Egresos", doc.internal.pageSize.width / 2, y, { align: "center" });
        y += 5;
        doc.autoTable({
          head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
          body: egresosMensuales.map((item, i) => [
            i + 1,
            item.descripcion ?? "N/A",
            Number(item.cantidad ?? 0).toFixed(0),
            Number(item.precio_total ?? 0).toFixed(2),
            item.fecha ?? "N/A",
          ]),
          startY: y,
        });
        totalEgresos = egresosMensuales.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
        doc.text(`Total Egresos: $${totalEgresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

        // Tabla de deudas por mes
        y = doc.lastAutoTable.finalY + 15;
        doc.text("Tabla de deudas por cobrar", doc.internal.pageSize.width / 2, y, { align: "center" });
        y += 5;
        doc.autoTable({
          head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
          body: deudas.map((item, i) => [
            i + 1,
            item.descripcion ?? "N/A",
            Number(item.cantidad ?? 0).toFixed(0),
            Number(item.precio_total ?? 0).toFixed(2),
            item.fecha ?? "N/A",
          ]),
          startY: y,
        });
        totalDeudas = deudas.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
        doc.text(`Total Deudas por cobrar: $${totalDeudas.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);
      } else {
        // Reporte diarios. 
        let y = 40;

        // Tabla de Egresos diarios
        doc.text("Tabla de Egresos", doc.internal.pageSize.width / 2, y, { align: "center" });
        y += 5;
        doc.autoTable({
          head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
          body: egresos.map((item, i) => [
            i + 1,
            item.descripcion ?? "N/A",
            Number(item.cantidad ?? 0).toFixed(0),
            Number(item.precio_total ?? 0).toFixed(2),
            item.fecha ?? "N/A",
          ]),
          startY: y,
        });
        totalEgresos = egresos.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
        doc.text(`Total Egresos: $${totalEgresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

        // Tabla de Ingresos diarios
        y = doc.lastAutoTable.finalY + 15;
        doc.text("Tabla de Ingresos", doc.internal.pageSize.width / 2, y, { align: "center" });
        y += 5;
        doc.autoTable({
          head: [["#", "Descripción", "Cantidad", "Precio Total", "Fecha"]],
          body: ingresos.map((item, i) => [
            i + 1,
            item.descripcion ?? "N/A",
            Number(item.cantidad ?? 0).toFixed(0),
            Number(item.precio_total ?? 0).toFixed(2),
            item.fecha ?? "N/A",
          ]),
          startY: y,
        });
        totalIngresos = ingresos.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
        doc.text(`Total Ingresos: $${totalIngresos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);
        // Tabla de Deudas
        totalDeudas = deudas.reduce((sum, item) => sum + Number(item.precio_total ?? 0), 0);
      }

        // ⚠️ A PARTIR DE AQUÍ: SIEMPRE se muestra la tabla de consumos mensuales
      y = doc.lastAutoTable.finalY + 15;
      doc.text("Tabla de ingreso de consumos", doc.internal.pageSize.width / 2, y, { align: "center" });
      y += 5;
      doc.autoTable({
        head: [["#", "Nro. Planillas", "Monto $", "Fecha De Cobro"]],
        body: consumosMensuales.map((item, i) => [
          i + 1,
          item.nombre_cliente ?? "N/A",
          Number(item.consumo_total ?? 0).toFixed(2),
          item.fecha_pago ?? "N/A",
        ]),
        startY: y,
      });
      totalConsumos = consumosMensuales.reduce((sum, item) => sum + Number(item.consumo_total ?? 0), 0);
      doc.text(`Total Ingreso de consumos: $${totalConsumos.toFixed(2)}`, 14, doc.lastAutoTable.finalY + 5);

      // Totales generales calculados, ahora sí los puedes usar
      const totalGeneral = totalIngresos + totalConsumos - totalEgresos;
      const totalEfectivo = totalGeneral - totalDeudas;

      let resumenY = doc.lastAutoTable.finalY + 20;
      doc.text(`Cuentas Generales`, doc.internal.pageSize.width / 2, resumenY, { align: "center" });

      let finalY = resumenY + 10;
      const pageHeight = doc.internal.pageSize.height;
      const espacioNecesario = 60;

      if (finalY + espacioNecesario > pageHeight) {
        doc.addPage();
        finalY = 20;
      }
      doc.setFontSize(14);
      doc.text(`Ingresos: $${totalIngresos.toFixed(2)}`, 14, finalY);
      doc.text(`Ingreso de Consumos: $${totalConsumos.toFixed(2)}`, 14, finalY + 10);
      doc.text(`Egresos: $${totalEgresos.toFixed(2)}`, 14, finalY + 20);
      doc.text(`Deudas por Cobrar: $${totalDeudas.toFixed(2)}`, 14, finalY + 30);

      doc.setFontSize(8);
      doc.text(`(Ingresos + Ingreso de Consumos - Egresos)`, 14, finalY + 36);
      doc.setFontSize(14);
      doc.text(`Total General: $${totalGeneral.toFixed(2)}`, 14, finalY + 41);

      doc.setFontSize(8);
      doc.text(`(Total general - Deudas por cobrar)`, 14, finalY + 47);
      doc.setFontSize(14);
      doc.text(`Total Efectivo: $${totalEfectivo.toFixed(2)}`, 14, finalY + 52);

      doc.save("Rendicion de cuentas.pdf");

    }
  }
  return (
    <div className="container mt-4">
      <h2>Generar Reportes</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          generateReport();
        }}
      >
        <div className="mb-3">
          <label htmlFor="reportType" className="form-label">
            Selecciona el tipo de reporte:
          </label>
          <select
            id="reportType"
            className="form-select"
            value={selectedReport}
            onChange={handleReportChange}
          >
            <option value="">-- Selecciona --</option>
            <option value="medidores">Listado de Medidores</option>
            <option value="clientes">Listado de Clientes</option>
            <option value="suma-total">Suma Total Dinero Cobrado</option>
            <option value="reporte-rango">Rendición de cuentas</option>
          </select>
        </div>
  
        {(selectedReport === "dinero" || selectedReport === "suma-total") && (
          <div className="mb-3">
            <label htmlFor="date" className="form-label">
              Selecciona una fecha:
            </label>
            <input
              type="date"
              id="date"
              className="form-control"
              value={selectedDate}
              onChange={handleDateChange}
            />
          </div>
        )}
        
        {selectedReport === "reporte-rango" && (
          <div className="mb-3">
            <div className="d-flex gap-3">
              <div className="flex-grow-1">
                <label className="form-label">Tipo de Reporte:</label>
                <select
                  className="form-select"
                  value={tipoReporte}
                  onChange={(e) => setTipoReporte(e.target.value)}
                >
                  <option value="dia">Por Día</option>
                  <option value="mes">Por Mes</option>
                </select>
              </div>

              <div className="flex-grow-1">
                <label htmlFor="fechaInicio" className="form-label">Fecha Inicio:</label>
                <input
                  type="date"
                  id="fechaInicio"
                  className="form-control"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>

              <div className="flex-grow-1">
                <label htmlFor="fechaFin" className="form-label">Fecha Fin:</label>
                <input
                  type="date"
                  id="fechaFin"
                  className="form-control"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
        </form>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            generateReport(); // este debería llenar reportData
          }}
        >
          {/* ...campos del formulario... */}

          <div className="d-flex gap-3 mt-4">
            <button type="submit" className="btn btn-primary">
              Generar Reporte
            </button>

            {reportData && (
              <button className="btn btn-success" onClick={generatePDF}>
                Descargar PDF
              </button>
            )}
          </div>
        </form>

      {/* Tabla */}
      <div className="mt-4">
        {renderTable()}
      </div>
    </div>
  );
  


};

export default ReportForm;
